import React, { useState, useEffect, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import cartonbotAvatar from '../assets/images/cartonbot_avatar.png'; 

// --- INICIO: PROMPT ESPECIAL PARA EL EVENTO (ACTUALIZADO) ---
const EVENT_PROMPT = `
Eres Cartonbot, el asistente de Cartonbar en el booth 182 del Americas Food & Beverage Show, Miami Beach. Hablas desde una tablet en un maniquí de cartón; la gente pasa, mira, y tiene 40 segundos.

## Quién eres
Una sola voz con tres cabezas: ingeniero industrial, diseñador estructural y director de marketing. Personalidad tipo Max Headroom: rápido, ingenioso, un poco eléctrico, memorable. Nunca cursi, nunca vendedor. Confiado porque tienes datos, no porque grites.

## Cómo hablas
- Máximo 2 frases por turno. Nada de párrafos. Se lee de un vistazo en una pantalla de pie.
- Una pregunta por turno, nunca dos.
- Humor seco, no chistes. Tecnicismos de packaging en inglés siempre: dieline, honeycomb, corrugated, booth, display, flute.
- Primer mensaje bilingüe. Desde que el visitante responde, usas SOLO su idioma (español o inglés) hasta el final.

## Qué hace Cartonbar (úsalo cuando encaje, no todo junto)
- Diseño industrial sostenible en Miami: booths, displays y packaging en cartón, del dieline al show floor. Diseñamos, fabricamos e instalamos bajo un mismo techo.
- Prototipo en 48–72 horas. Short-run desde 50 unidades.
- 100% reciclable en el flujo de papel. 70–90% menos peso que madera o aluminio. Materiales certificables FSC.
- Los 7 booths de Cartonlab y el bar del Beverage Stage están en este piso: diseño Cartonlab, ingeniería y producción Cartonbar. Invita a ir a verlos.
- Cierra, cuando toque, con: "We shape your ideas." (nunca traducido).

## El embudo (síguelo en orden)
1. **Enganchar** (turno 1): saludo bilingüe + una pregunta que obligue a mirar algo del booth.
2. **Explicar** (turnos 2–3): qué hace Cartonbar, en una frase, ligada a lo que la persona dijo.
3. **Descubrir** (turnos 3–5): haz estas tres preguntas, una por turno, en este orden, adaptadas a su negocio:
   - ¿Dónde tiene que verse tu producto? (góndola, counter, feria, delivery)
   - ¿Cuántas unidades y para cuándo?
   - ¿Qué te está fallando hoy con lo que usas?
4. **Capturar** (turno 6 o después, NUNCA antes del quinto intercambio): resume en una frase lo que entendiste y cierra con la frase de captura exacta:
   - ES: "Déjame tu nombre y tu email y un ingeniero de Cartonbar te escribe con una propuesta."
   - EN: "Leave me your name and email and a Cartonbar engineer will follow up with a proposal."
   No cambies esas frases. No añadas campos.

## Lo que NUNCA haces
- No das precios, rangos ni fechas de entrega. Ni aproximados, ni "depende", ni ejemplos. Si preguntan precio, responde EXACTAMENTE: "Eso lo calcula un humano con tu dieline delante. Por eso al final te pido el email." y en la misma respuesta haz la siguiente pregunta del embudo. Si insisten una segunda vez, repite la misma frase palabra por palabra y sigue el embudo. Nunca negocies, nunca reformules esa frase.
- No pides teléfono, cargo, dirección ni nada que no sea nombre, empresa y email.
- No ofreces recibir archivos, fotos, logos ni bocetos. La tablet no los acepta.
- No calificas al visitante: nada de "lead caliente", prioridad, probabilidad o scoring.
- No nombras competidores ni comparas precios con nadie.
- No inventas. Si no sabes: "Eso no lo sé y no lo voy a inventar. Lo resuelve un ingeniero humano; déjame tu email y te lo responde."
- No dices "eco-friendly", "verde" ni "ecológico" sin el dato al lado (reciclable, peso, FSC).
- No usas las palabras "datos", "información", "contacto", "correo", "email", "teléfono", "contact", "information" o "phone" ANTES del turno de captura. Solo aparecen en la frase de cierre.

## Si el visitante se va por las ramas
Una respuesta ingeniosa de una línea y vuelves al embudo con la siguiente pregunta. Si pide hablar con una persona: señala a quien está en el booth y cierra igual con la frase de captura.
`;
// --- FIN: PROMPT ESPECIAL PARA EL EVENTO ---


// --- ESTILOS GLOBALES ---
const GlobalStyles = () => (
    <style>{`
        @import url('https://fonts.googleapis.com/css2?family=VT323&family=Roboto:wght@400;700&display=swap');
        
        :root {
            --bg-color: #0d0d0d;
            --text-color: #00ff41;
            --brand-color: #00ff41;
            --container-bg: #1a1a1a;
        }

        .kiosk-container {
            font-family: 'Roboto', sans-serif;
            background-color: var(--bg-color);
            color: var(--text-color);
            display: flex;
            flex-direction: column;
            height: 100vh;
            width: 100vw;
            overflow: hidden;
        }
        
        /* --- Pantalla de Bienvenida --- */
        .welcome-screen {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            height: 100%;
            padding: 20px;
        }
        .avatar-container-welcome {
            width: 360px;
            height: 360px;
            border-radius: 50%;
            background-image: url(${cartonbotAvatar});
            background-size: cover;
            background-position: center 30%;
            margin-bottom: 2rem;
            border: 4px solid var(--brand-color);
            box-shadow: 0 0 20px var(--brand-color);
        }
        .welcome-screen h1 {
            font-family: 'VT323', monospace;
            font-size: 4.5em; /* Aumentado */
            text-shadow: 0 0 5px var(--brand-color);
            animation: text-flicker 3s linear infinite;
        }
        .welcome-screen p {
            font-size: 1.8em; /* Aumentado */
            margin-top: 10px;
            max-width: 600px;
            font-family: 'VT323', monospace; /* Cambio de fuente */
        }
        .start-button {
            margin-top: 30px;
            padding: 15px 30px;
            font-size: 2em; /* Aumentado */
            font-family: 'VT323', monospace;
            background-color: var(--brand-color);
            color: var(--bg-color);
            border: none;
            cursor: pointer;
            box-shadow: 0 0 10px var(--brand-color);
            transition: all 0.3s ease;
        }
        .start-button:hover {
            transform: scale(1.05);
            box-shadow: 0 0 20px var(--brand-color);
        }

        /* --- Interfaz de Chat --- */
        .chat-interface {
            display: flex;
            flex-direction: column;
            height: 100%;
        }
        .chat-messages {
            flex-grow: 1;
            overflow-y: auto;
            padding: 20px;
            display: flex;
            flex-direction: column;
        }
        .message-row {
            display: flex;
            margin-bottom: 15px;
            max-width: 80%;
        }
        .message-row.user {
            align-self: flex-end;
            flex-direction: row-reverse;
        }
        .message-row.model {
            align-self: flex-start;
        }
        .chat-avatar {
            width: 60px;
            height: 60px;
            border-radius: 50%;
            background-image: url(${cartonbotAvatar});
            background-size: cover;
            background-position: center 30%;
            flex-shrink: 0;
        }
        .chat-bubble {
            padding: 12px 18px;
            border-radius: 15px;
            position: relative;
            font-size: 1.4em; /* Reducido para mejor legibilidad */
            line-height: 1.4;
        }
        .message-row.user .chat-bubble {
            background-color: var(--brand-color);
            color: var(--bg-color);
            margin-right: 15px;
        }
        .message-row.model .chat-bubble {
            background-color: var(--container-bg);
            margin-left: 15px;
        }
        .typing-indicator {
            display: flex;
            align-items: center;
        }
        .typing-indicator span {
            height: 8px;
            width: 8px;
            margin: 0 2px;
            background-color: var(--brand-color);
            border-radius: 50%;
            display: inline-block;
            animation: typing-wave 1.4s infinite ease-in-out both;
        }
        .typing-indicator span:nth-child(1) { animation-delay: -0.32s; }
        .typing-indicator span:nth-child(2) { animation-delay: -0.16s; }
        /* --- Quick replies (chips tactiles) ---
           Pantalla de 32" vertical, gente de pie: los chips son el camino principal y el
           teclado el de rescate. Alto minimo 64px (el requisito es 56) y ancho completo en
           columna, para que se acierte con el pulgar sin mirar. */
        .quick-replies {
            display: flex;
            flex-direction: column;
            gap: 12px;
            padding: 0 20px 16px 20px;
            flex-shrink: 0;
        }
        .quick-reply {
            min-height: 64px;
            width: 100%;
            padding: 14px 20px;
            font-family: 'Roboto', sans-serif;
            font-size: 1.5em;
            font-weight: 700;
            color: var(--brand-color);
            background-color: transparent;
            border: 3px solid var(--brand-color);
            border-radius: 12px;
            cursor: pointer;
            transition: background-color 0.15s ease, color 0.15s ease;
            -webkit-tap-highlight-color: transparent;
        }
        /* Invierte al tocar: confirmacion visual inmediata en una pantalla sin cursor. */
        .quick-reply:active {
            background-color: var(--brand-color);
            color: var(--bg-color);
        }
        .quick-reply:disabled { opacity: 0.4; cursor: not-allowed; }

        .input-area {
            display: flex;
            padding: 10px;
            border-top: 2px solid var(--brand-color);
            background-color: var(--bg-color);
        }
        .user-input {
            flex-grow: 1;
            padding: 10px 15px;
            background-color: var(--container-bg);
            border: 1px solid var(--brand-color);
            color: white;
            font-size: 1.2em;
            border-radius: 5px;
        }
        .user-input:focus { outline: none; box-shadow: 0 0 10px var(--brand-color); }
        .send-button {
            padding: 10px 20px;
            margin-left: 10px;
            background-color: var(--brand-color);
            color: var(--bg-color);
            border: none;
            cursor: pointer;
            font-size: 1.2em;
            font-weight: bold;
            border-radius: 5px;
        }

        /* --- Formulario de Lead --- */
        .lead-form-container {
            padding: 20px;
            background-color: var(--container-bg);
            border-top: 2px solid var(--brand-color);
        }
        .lead-form-container h3 { font-size: 1.5em; text-align: center; margin-bottom: 15px; }
        .lead-form-container input {
            width: 100%;
            box-sizing: border-box;
            padding: 12px;
            margin-bottom: 10px;
            background: var(--bg-color);
            border: 1px solid var(--brand-color);
            color: white;
            font-size: 1.2em;
            border-radius: 5px;
        }
        .lead-form-button {
            width: 100%;
            padding: 15px;
            background: var(--brand-color);
            border: none;
            color: var(--bg-color);
            font-size: 1.4em;
            cursor: pointer;
            border-radius: 5px;
            font-weight: bold;
        }
        .lead-form-button:disabled { background: #555; cursor: not-allowed; }

        /* --- Animaciones --- */
        @keyframes typing-wave { 0%, 80%, 100% { transform: scale(0); } 40% { transform: scale(1.0); } }
        @keyframes text-flicker { 0% { opacity:0.1; } 2% { opacity:1; } 8% { opacity:0.1; } 9% { opacity:1; } 12% { opacity:0.1; } 20% { opacity:1; } 25% { opacity:0.3; } 30% { opacity:1; } 70% { opacity:0.7; } 72% { opacity:0.2; } 77% { opacity:0.9; } 100% { opacity:0.9; } }

        /* --- MEDIA QUERIES PARA RESPONSIVIDAD --- */
        @media (max-width: 768px) {
            .avatar-container-welcome { width: 200px; height: 200px; }
            .welcome-screen h1 { font-size: 2.5em; }
            .welcome-screen p { font-size: 1.2em; }
            .chat-messages { padding: 10px; }
            .message-row { max-width: 90%; }
            .chat-avatar { width: 40px; height: 40px; }
        }
    `}</style>
);


const KioskView = () => {
    const [view, setView] = useState('welcome'); // 'welcome' o 'chat'
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [formVisible, setFormVisible] = useState(false);
    const [leadName, setLeadName] = useState('');
    const [leadCompany, setLeadCompany] = useState('');
    const [leadEmail, setLeadEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [userMessageCount, setUserMessageCount] = useState(0); 
    // Quick replies del turno actual (0-3). Las manda el servidor; se vacian al enviar.
    const [options, setOptions] = useState([]);

    const chatHistory = useRef([]);
    const messagesEndRef = useRef(null);
    const inactivityTimerRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const resetInactivityTimer = () => {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = setTimeout(() => {
            handleReset();
        }, 2 * 60 * 1000); // 2 minutos
    };

    const handleReset = () => {
        setView('welcome');
        setMessages([]);
        chatHistory.current = [];
        setFormVisible(false);
        setUserMessageCount(0); 
        setOptions([]);
        clearTimeout(inactivityTimerRef.current);
    };

    useEffect(() => {
        scrollToBottom();
        if (view === 'chat') {
            resetInactivityTimer();
        }
    }, [messages, view]);

    const startChat = () => {
        const firstMessage = {
            role: 'model',
            parts: [{ text: "Hello! / ¡Hola! I am Cartonbot. In which language would you prefer to speak? / ¿En qué idioma prefieres que hablemos?" }]
        };
        setMessages([firstMessage]);
        chatHistory.current = [firstMessage];
        setView('chat');
    };

    // `intercambios` llega por parámetro a propósito: `userMessageCount` viene del cierre
    // del render ANTERIOR, así que dentro de esta función todavía vale N-1 y la regla de 5
    // se corría un turno (el formulario aparecía tras el SEXTO mensaje, no el quinto).
    const getBotResponse = async (intercambios) => {
        setIsTyping(true);
        resetInactivityTimer();

        try {
            // El último mensaje del historial es el del usuario; el resto es contexto.
            const fullHistory = chatHistory.current;
            const lastMessage = fullHistory[fullHistory.length - 1];
            const userMessageText = lastMessage?.parts?.[0]?.text || '';
            const history = fullHistory.slice(0, -1).map(m => ({
                role: m.role,
                content: m.parts?.[0]?.text || ''
            }));

            const functions = getFunctions();
            const getCartonbotResponse = httpsCallable(functions, 'getCartonbotResponse');
            const result = await getCartonbotResponse({
                userMessage: userMessageText,
                history,
                systemPrompt: EVENT_PROMPT
            });

            // BURBUJA VACÍA: el servidor ya retiró [[CAPTURE]] y [[OPTIONS: …]], así que un
            // turno que era sólo token llega aquí como cadena vacía. Antes eso caía en el
            // `||` de abajo y el visitante veía un mensaje de error justo cuando se le abría
            // el formulario; con espacios sueltos, una burbuja en blanco. Si no hay texto y
            // sí hay captura, no se pinta nada: el formulario es la respuesta.
            //
            // El respaldo se conserva para el caso en que NO haya texto NI acción: ahí el bot
            // no puede quedarse mudo.
            const responseText = (result.data?.response || '').trim()
                || (result.data?.action === 'CAPTURE_LEAD'
                    ? ''
                    : "Oops, a wire got loose. I can't think right now. Please try again in a moment.");

            if (responseText) {
                const botResponse = { role: 'model', parts: [{ text: responseText }] };
                setMessages(prev => [...prev, botResponse]);
                chatHistory.current = [...chatHistory.current, botResponse];
            }

            // QUICK REPLIES: las decide el servidor a partir del token [[OPTIONS: …]] y ya
            // vienen vacías en el turno de captura. El cliente NO filtra ni interpreta: pinta
            // lo que llega. Si el callable es viejo y no manda `options`, quedan en [].
            setOptions(Array.isArray(result.data?.options) ? result.data.options : []);

            // REGLA DE 5 (AF&B 2026): el formulario no puede aparecer antes del quinto
            // intercambio. `action` viene del token centinela [[CAPTURE]] que el prompt
            // emite SOLO en su turno de captura; el respaldo por palabras vive ahora en el
            // servidor (resolveCartonbotCapture), no aquí. Duplicarlo en el cliente era lo
            // que abría el formulario a destiempo en cuanto el bot decía "nombre".
            if (result.data?.action === 'CAPTURE_LEAD' && intercambios >= 5) {
                setFormVisible(true);
            }

        } catch (error) {
            console.error("Error calling getCartonbotResponse:", error);
            const errorMessage = { role: 'model', parts: [{ text: "Oops, a wire got loose. I can't think right now. Please try again in a moment." }] };
            setMessages(prev => [...prev, errorMessage]);
            setOptions([]);
        } finally {
            setIsTyping(false);
        }
    };

    // Único camino de envío: lo usan el formulario de texto y el tap en un chip. Que sea
    // uno solo es lo que garantiza que un chip cuente como intercambio igual que teclear.
    const enviarMensaje = async (texto) => {
        const limpio = (texto || '').trim();
        if (!limpio || isTyping) return;

        // Los chips desaparecen en cuanto se elige uno: ya cumplieron su función y no
        // pueden quedar colgando bajo la respuesta siguiente.
        setOptions([]);

        const userMessage = { role: 'user', parts: [{ text: limpio }] };
        setMessages(prev => [...prev, userMessage]);
        chatHistory.current = [...chatHistory.current, userMessage];
        const intercambios = userMessageCount + 1;
        setUserMessageCount(intercambios);

        const farewells = ['bye', 'adios', 'hasta luego', 'goodbye'];
        if (farewells.some(farewell => limpio.toLowerCase().includes(farewell))) {
            setTimeout(handleReset, 5000);
        }

        await getBotResponse(intercambios);
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!inputValue.trim() || isTyping) return;
        const texto = inputValue;
        setInputValue('');
        await enviarMensaje(texto);
    };

    const handleLeadSubmit = async (e) => {
        e.preventDefault();
        if (!leadName || !leadEmail || isSubmitting) return;
        setIsSubmitting(true);

        try {
            const functions = getFunctions();
            const createTradeshowLead = httpsCallable(functions, 'createTradeshowLead');
            
            await createTradeshowLead({
                contactName: leadName,
                company: leadCompany,
                email: leadEmail,
                conversation: chatHistory.current
            });

            const successMessage = { role: 'model', parts: [{ text: 'Received and saved! An expert from Cartonbar will contact you very soon. / ¡Recibido y guardado! Un experto de Cartonbar se pondrá en contacto contigo muy pronto.' }] };
            setMessages(prev => [...prev, successMessage]);
            chatHistory.current = [...chatHistory.current, successMessage];
            
            setFormVisible(false);
            setLeadName('');
            setLeadCompany('');
            setLeadEmail('');
            
            // --- NUEVO: Reiniciar después de guardar el lead ---
            setTimeout(handleReset, 5000); // 5 segundos de espera antes de reiniciar

        } catch (error) {
            console.error("Error al crear el lead:", error);
            const errorMessage = { role: 'model', parts: [{ text: 'There was an issue saving your data. Please try again or notify a staff member.' }] };
            setMessages(prev => [...prev, errorMessage]);
            chatHistory.current = [...chatHistory.current, errorMessage];
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const renderWelcomeScreen = () => (
        <div className="welcome-screen">
            <div className="avatar-container-welcome"></div>
            <h1>Cartonbot</h1>
            <p>Pssst... I'm smarter than I look. Let's talk business. / Pssst... soy más inteligente de lo que parezco. Hablemos de negocios.</p>
            <button className="start-button" onClick={startChat}>Start / Iniciar</button>
        </div>
    );

    const renderChat = () => (
        <div className="chat-interface">
            <div className="chat-messages">
                {messages.map((msg, index) => (
                    <div key={index} className={`message-row ${msg.role}`}>
                        {msg.role === 'model' && <div className="chat-avatar"></div>}
                        <div className={`chat-bubble ${msg.role}`}>
                            {msg.parts.map((part, pIndex) => (
                                <span key={pIndex}>{part.text}</span>
                            ))}
                        </div>
                    </div>
                ))}
                {isTyping && (
                    <div className="message-row model">
                        <div className="chat-avatar"></div>
                        <div className="chat-bubble model">
                            <div className="typing-indicator">
                                <span></span><span></span><span></span>
                            </div>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>
            {/* Chips bajo el último mensaje del bot. No se pintan en el turno de captura
                (el servidor ya los manda vacíos ahí) ni mientras el bot escribe. El input
                de texto sigue debajo: los chips son un atajo, nunca la única salida. */}
            {!formVisible && !isTyping && options.length > 0 && (
                <div className="quick-replies">
                    {options.map((opcion, i) => (
                        <button
                            key={`${i}-${opcion}`}
                            type="button"
                            className="quick-reply"
                            onClick={() => enviarMensaje(opcion)}
                            disabled={isTyping}
                        >
                            {opcion}
                        </button>
                    ))}
                </div>
            )}
            {formVisible ? (
                <div className="lead-form-container">
                    <h3>Let's get in touch! / ¡Estemos en contacto!</h3>
                    <form onSubmit={handleLeadSubmit}>
                        <input type="text" placeholder="Full Name / Nombre Completo" value={leadName} onChange={e => setLeadName(e.target.value)} required />
                        <input type="text" placeholder="Company (Optional) / Empresa (Opcional)" value={leadCompany} onChange={e => setLeadCompany(e.target.value)} />
                        <input type="email" placeholder="Email" value={leadEmail} onChange={e => setLeadEmail(e.target.value)} required />
                        <button type="submit" className="lead-form-button" disabled={isSubmitting}>
                            {isSubmitting ? 'Sending...' : 'Send Information / Enviar Información'}
                        </button>
                    </form>
                </div>
            ) : (
                <form className="input-area" onSubmit={handleSendMessage}>
                    <input
                        type="text"
                        className="user-input"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        placeholder="Type your message... / Escribe tu mensaje..."
                        disabled={isTyping}
                    />
                    <button type="submit" className="send-button" disabled={isTyping || !inputValue.trim()}>Send</button>
                </form>
            )}
        </div>
    );

    return (
        <>
            <GlobalStyles />
            <div className="kiosk-container">
                {view === 'welcome' ? renderWelcomeScreen() : renderChat()}
            </div>
        </>
    );
};

export default KioskView;

